import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type {
  Category,
  CreateDealInput,
  DealListResponse,
  DealSort,
  DealSummary,
  Tag,
  UpdateDealInput,
  UploadResponse,
  PurchaseAdvice,
  PriceAlert,
  PriceHistory,
} from "@bargain-radar/shared";
import { apiGet, apiSend } from "../lib/api";

export type FeedFilters = {
  sort?: DealSort;
  category?: string;
  tag?: string;
  q?: string;
  merchant?: string;
  page?: number;
};

function feedKey(f: FeedFilters): readonly unknown[] {
  return ["feed", f.sort ?? "hot", f.category ?? null, f.tag ?? null, f.q ?? null, f.merchant ?? null, f.page ?? null];
}

function feedParams(f: FeedFilters, cursor?: string | null): string {
  const u = new URLSearchParams();
  if (f.sort) u.set("sort", f.sort);
  if (f.category) u.set("category", f.category);
  if (f.tag) u.set("tag", f.tag);
  if (f.q) u.set("q", f.q);
  if (f.merchant) u.set("merchant", f.merchant);
  if (f.page) u.set("page", String(f.page));
  if (cursor) u.set("cursor", cursor);
  return u.toString();
}

export function feedQueryOptions(f: FeedFilters) {
  return infiniteQueryOptions({
    queryKey: feedKey(f),
    queryFn: async ({ pageParam }) => {
      return apiGet<DealListResponse>(`/api/deals?${feedParams(f, pageParam ? String(pageParam) : null)}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function feedPageQueryOptions(f: FeedFilters & { page: number }) {
  return queryOptions({
    queryKey: feedKey(f),
    queryFn: () => apiGet<DealListResponse>(`/api/deals?${feedParams(f)}`),
  });
}

export function dealQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["deal", id],
    queryFn: () => apiGet<DealSummary>(`/api/deals/${encodeURIComponent(id)}`),
  });
}

export function createDeal(input: CreateDealInput) {
  return apiSend<{ id: string; slug: string }>("POST", "/api/deals", input);
}

export function updateDeal(id: string, input: UpdateDealInput) {
  return apiSend<{ ok: true }>("PATCH", `/api/deals/${encodeURIComponent(id)}`, input);
}

export function deleteDeal(id: string) {
  return apiSend<void>("DELETE", `/api/deals/${encodeURIComponent(id)}`);
}

export function requestPurchaseAdvice(id: string) {
  return apiSend<PurchaseAdvice>("POST", `/api/deals/${encodeURIComponent(id)}/advice`);
}

export function priceHistoryQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["price-history", id],
    queryFn: () => apiGet<PriceHistory>(`/api/deals/${encodeURIComponent(id)}/price-history`),
  });
}

export const alertsQueryOptions = queryOptions({
  queryKey: ["price-alerts"],
  queryFn: () => apiGet<PriceAlert[]>("/api/alerts"),
});

export function setPriceAlert(id: string, targetPriceCents: number) {
  return apiSend<{ id: string }>("POST", `/api/deals/${encodeURIComponent(id)}/alert`, { targetPriceCents });
}

export function deletePriceAlert(id: string) {
  return apiSend<{ ok: true }>("DELETE", `/api/deals/${encodeURIComponent(id)}/alert`);
}

export function sendAdviceFeedback(id: string, priceCents: number, helpful: boolean) {
  return apiSend<{ ok: true }>("POST", `/api/deals/${encodeURIComponent(id)}/advice/feedback`, { priceCents, helpful });
}

export const categoriesQueryOptions = queryOptions({
  queryKey: ["categories"],
  queryFn: () => apiGet<Category[]>("/api/categories"),
  staleTime: 5 * 60_000,
});

export function tagsTypeaheadQueryOptions(prefix: string) {
  return queryOptions({
    queryKey: ["tags", prefix],
    queryFn: () =>
      prefix.length === 0
        ? Promise.resolve<Tag[]>([])
        : apiGet<Tag[]>(`/api/tags?prefix=${encodeURIComponent(prefix)}`),
    enabled: prefix.length > 0,
  });
}

export async function uploadImage(file: File): Promise<UploadResponse> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch("/api/uploads", {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `Upload failed (${res.status})`);
  }
  return (await res.json()) as UploadResponse;
}
